import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LayoutGrid, Compass, Award, BookOpen, HelpCircle, Home, Check, Shield } from "lucide-react";
import { UserButton } from "@clerk/react";
import { useMe } from "@/components/auth/AccessGate";
import { Logo } from "@/components/Logo";
import { ProgressBar } from "@/components/kit";
import { useProgress } from "@/modules/premiere-video/store";
import { MISSIONS } from "@/modules/premiere-video/content";
import { doneCount, missionStatus, resumeMission } from "@/modules/premiere-video/logic";

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] transition-colors ${isActive ? "bg-bx text-white" : "text-ink hover:bg-bx/5"}`;

function Sidebar() {
  const { state } = useProgress();
  const me = useMe();
  const done = doneCount(state);
  return (
    <aside className="no-print fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-line bg-white/70 px-5 py-6 backdrop-blur-xl lg:flex">
      <Logo />
      <div className="mt-7 rounded-2xl bg-ivory p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-soft">Ma première vidéo UGC</p>
        <p className="mt-2 text-sm text-ink" data-testid="sidebar-progress-label">
          <span className="font-display text-2xl text-bx">{done}</span> / 7 missions
        </p>
        <ProgressBar value={Math.round((done / 7) * 100)} className="mt-2" />
      </div>
      <nav className="mt-6 flex-1 space-y-1 overflow-y-auto" aria-label="Navigation principale">
        <NavLink to="/mon-espace" className={linkCls} data-testid="nav-home"><Home className="h-4 w-4" strokeWidth={1.6} />Mon espace UGC & CO</NavLink>
        <NavLink to="/parcours" className={linkCls} data-testid="nav-dashboard"><LayoutGrid className="h-4 w-4" strokeWidth={1.6} />Tableau de bord</NavLink>
        <p className="px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-[0.18em] text-soft">Missions</p>
        {MISSIONS.map((m) => {
          const st = missionStatus(state, m.id);
          return (
            <NavLink key={m.id} to={`/mission/${m.id}`} className={linkCls} data-testid={`nav-mission-${m.id}`}>
              {({ isActive }) => (
                <>
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${st === "done" ? (isActive ? "bg-white text-bx" : "bg-bx text-white") : isActive ? "border border-white/60" : "border border-gold text-[#8E6E3C]"}`}>
                    {st === "done" ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : m.num}
                  </span>
                  <span className="truncate">{m.title}</span>
                </>
              )}
            </NavLink>
          );
        })}
        <div className="pt-4" />
        <NavLink to="/resultats" className={linkCls} data-testid="nav-results"><Award className="h-4 w-4" strokeWidth={1.6} />Mes résultats</NavLink>
        <NavLink to="/ressources" className={linkCls} data-testid="nav-resources"><BookOpen className="h-4 w-4" strokeWidth={1.6} />Ressources</NavLink>
        <NavLink to="/faq" className={linkCls} data-testid="nav-faq"><HelpCircle className="h-4 w-4" strokeWidth={1.6} />FAQ</NavLink>
        {me?.role === "admin" && <NavLink to="/admin" className={linkCls} data-testid="nav-admin"><Shield className="h-4 w-4" strokeWidth={1.6} />Administration</NavLink>}
      </nav>
      <div className="mt-4 flex items-center gap-3 border-t border-line pt-4" data-testid="sidebar-account">
        <UserButton />
        <span className="text-sm text-soft">Mon compte</span>
      </div>
    </aside>
  );
}

function MobileTop() {
  const { state } = useProgress();
  const done = doneCount(state);
  return (
    <header className="no-print sticky top-0 z-40 flex items-center justify-between border-b border-line/80 bg-ivory/90 px-5 py-3 backdrop-blur-xl lg:hidden">
      <Logo />
      <div className="flex items-center gap-3">
      <NavLink to="/parcours" data-testid="mobile-progress-pill" className="flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1.5 text-sm font-medium text-bx">
        <span className="h-1.5 w-12 overflow-hidden rounded-full bg-[#EFE8E1]">
          <span className="block h-full bg-bx" style={{ width: `${(done / 7) * 100}%` }} />
        </span>
        {done}/7
      </NavLink>
      <UserButton />
      </div>
    </header>
  );
}

function MobileBottom() {
  const { state } = useProgress();
  const { pathname } = useLocation();
  const items = [
    { to: "/parcours", label: "Parcours", Icon: LayoutGrid, id: "dashboard" },
    { to: `/mission/${resumeMission(state)}`, label: "Missions", Icon: Compass, id: "missions", active: pathname.startsWith("/mission") },
    { to: "/resultats", label: "Résultats", Icon: Award, id: "results" },
    { to: "/ressources", label: "Ressources", Icon: BookOpen, id: "resources" },
    { to: "/faq", label: "FAQ", Icon: HelpCircle, id: "faq" },
  ];
  return (
    <nav className="no-print fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden" aria-label="Navigation mobile">
      <div className="grid grid-cols-5">
        {items.map(({ to, label, Icon, id, active }) => (
          <NavLink key={id} to={to} data-testid={`bottom-nav-${id}`} className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${active ?? isActive ? "text-bx" : "text-soft"}`}>
            {({ isActive }) => (
              <>
                <span className={`flex h-8 w-12 items-center justify-center rounded-full transition-colors ${active ?? isActive ? "bg-bx/[0.08]" : ""}`}>
                  <Icon className="h-5 w-5" strokeWidth={1.6} />
                </span>
                {label}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function AppLayout() {
  return (
    <div className="min-h-screen bg-ivory">
      <Sidebar />
      <MobileTop />
      <main className="pb-28 lg:pb-16 lg:pl-72 print:p-0">
        <div className="mx-auto w-full max-w-4xl px-5 py-8 sm:px-8 lg:py-12">
          <Outlet />
        </div>
      </main>
      <MobileBottom />
    </div>
  );
}
